// In-browser static site renderer — the "no-build viewer."
//
// BKA renders the finished static site in the creator's browser at
// publish time and commits ready-to-serve files; Cloudflare Pages serves
// the repo as static with NO build step (06_PUBLISH_PIPELINE.md, and the
// Sprint 2 / §4 open-questions decision). Sprint 6 adds themes
// (lib/themes.ts), an about/archive/tag route set, and a tiny client
// search — still zero build, zero runtime AI, zero tracking.
//
// All in-site links are root-absolute ("/posts/<slug>") because the site
// is served from the domain root; that keeps every page depth-agnostic.
// Clean URLs (no .html) work because CF Pages maps "/posts/foo" →
// posts/foo.html automatically.

import { marked } from "marked";
import { type CreatorProfile, type Post, postUrl, wordCount, slugify } from "./post";
import { themeCss } from "./themes";

export interface SiteFile {
  path: string;
  content: string;
}

marked.setOptions({ gfm: true, breaks: false });

function esc(s: string): string {
  return s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;").replace(/'/g, "&#39;");
}

function mdToHtml(markdown: string): string {
  return marked.parse(markdown) as string;
}

function fmtDate(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const y = d.getUTCFullYear();
  const m = String(d.getUTCMonth() + 1).padStart(2, "0");
  const day = String(d.getUTCDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

// ─── feed.json (10_FEED_SCHEMA.md) ──────────────────────────────────

export function renderFeedJson(profile: CreatorProfile, posts: Post[], generatedAt: string): string {
  const feed = {
    version: 1,
    generator: "bka-viewer/1.0.0",
    generatedAt,
    creator: {
      handle: profile.handle,
      displayName: profile.displayName,
      bio: profile.bio,
      siteUrl: profile.siteUrl,
      language: profile.language,
      discoverable: profile.discoverable,
    },
    items: posts.map((p) => ({
      slug: p.slug,
      title: p.title,
      summary: p.summary,
      url: postUrl(profile, p.slug),
      publishedAt: p.publishedAt,
      updatedAt: p.updatedAt,
      tags: p.tags,
      kinds: p.kinds,
      discoverable: profile.discoverable,
      language: p.language ?? profile.language,
      wordCount: wordCount(p.body),
      external: p.external ?? null,
    })),
  };
  return JSON.stringify(feed, null, 2) + "\n";
}

export function renderFeedXml(profile: CreatorProfile, posts: Post[]): string {
  const items = posts.map((p) => `    <item>
      <title>${esc(p.title)}</title>
      <link>${esc(postUrl(profile, p.slug))}</link>
      <guid isPermaLink="true">${esc(postUrl(profile, p.slug))}</guid>
      <description>${esc(p.summary)}</description>
      <pubDate>${new Date(p.publishedAt).toUTCString()}</pubDate>
    </item>`).join("\n");
  return `<?xml version="1.0" encoding="UTF-8"?>
<rss version="2.0">
  <channel>
    <title>${esc(profile.displayName)}</title>
    <link>${esc(profile.siteUrl)}</link>
    <description>${esc(profile.bio)}</description>
    <language>${esc(profile.language)}</language>
${items}
  </channel>
</rss>
`;
}

export function renderSitemap(profile: CreatorProfile, posts: Post[]): string {
  const urls = [
    profile.siteUrl + "/",
    profile.siteUrl + "/archive",
    profile.siteUrl + "/about",
    ...posts.map((p) => postUrl(profile, p.slug)),
  ];
  const body = urls.map((u) => `  <url><loc>${esc(u)}</loc></url>`).join("\n");
  return `<?xml version="1.0" encoding="UTF-8"?>
<urlset xmlns="http://www.sitemaps.org/schemas/sitemap/0.9">
${body}
</urlset>
`;
}

export function renderRobots(profile: CreatorProfile): string {
  return `User-agent: *\nAllow: /\nSitemap: ${profile.siteUrl}/sitemap.xml\n`;
}

// ─── HTML shell ─────────────────────────────────────────────────────

function page(profile: CreatorProfile, opts: { title: string; description: string; canonical: string; body: string; head?: string }): string {
  return `<!doctype html>
<html lang="${esc(profile.language)}">
<head>
<meta charset="UTF-8" />
<meta name="viewport" content="width=device-width, initial-scale=1.0" />
<title>${esc(opts.title)}</title>
<meta name="description" content="${esc(opts.description)}" />
<link rel="canonical" href="${esc(opts.canonical)}" />
<meta property="og:title" content="${esc(opts.title)}" />
<meta property="og:description" content="${esc(opts.description)}" />
<meta property="og:url" content="${esc(opts.canonical)}" />
<meta property="og:type" content="website" />
<meta name="twitter:card" content="summary" />
<link rel="alternate" type="application/rss+xml" title="RSS" href="/feed.xml" />
<link rel="stylesheet" href="/style.css" />
${opts.head ?? ""}
</head>
<body>
<a class="skip" href="#main">Skip to content</a>
<header class="site-head">
<a class="site-name" href="/">${esc(profile.displayName)}</a>
<nav class="site-nav" aria-label="primary">
<a href="/">Home</a>
<a href="/archive">Archive</a>
<a href="/about">About</a>
<a href="/feed.xml">RSS</a>
</nav>
</header>
<main class="site-main" id="main">
${opts.body}
</main>
<footer class="site-foot">
<span>Published on <a href="https://binary-blender.com/apps">BKA</a> — you are the platform.</span>
</footer>
</body>
</html>
`;
}

function postRow(p: Post): string {
  return `  <li class="post-row" data-search="${esc((p.title + " " + p.summary + " " + p.tags.join(" ")).toLowerCase())}">
    <a class="post-link" href="/posts/${esc(p.slug)}">${esc(p.title)}</a>
    <time class="post-date">${esc(fmtDate(p.publishedAt))}</time>
    <p class="post-summary">${esc(p.summary)}</p>
  </li>`;
}

const SEARCH_SCRIPT = `<script>
(function(){
  var box=document.getElementById('q'); if(!box) return;
  var rows=[].slice.call(document.querySelectorAll('.post-row'));
  box.addEventListener('input',function(){
    var q=box.value.trim().toLowerCase();
    rows.forEach(function(r){ r.style.display = !q || r.getAttribute('data-search').indexOf(q)>-1 ? '' : 'none'; });
  });
})();
</script>`;

export function renderIndexHtml(profile: CreatorProfile, posts: Post[]): string {
  const list = posts.length === 0
    ? `<p class="empty">No posts yet.</p>`
    : `<input id="q" class="searchbox" type="search" placeholder="Search posts…" aria-label="Search posts" />
<ul class="post-list">
${posts.map(postRow).join("\n")}
</ul>`;
  const body = `<section class="bio">
<h1 class="creator-name">${esc(profile.displayName)}</h1>
<p class="creator-bio">${esc(profile.bio)}</p>
</section>
${list}`;
  return page(profile, { title: profile.displayName, description: profile.bio, canonical: profile.siteUrl + "/", body, head: posts.length ? SEARCH_SCRIPT : "" });
}

export function renderArchiveHtml(profile: CreatorProfile, posts: Post[]): string {
  const body = `<h1 class="creator-name">Archive</h1>
${posts.length === 0 ? `<p class="empty">No posts yet.</p>` : `<ul class="post-list">
${posts.map(postRow).join("\n")}
</ul>`}`;
  return page(profile, { title: `Archive — ${profile.displayName}`, description: `All posts by ${profile.displayName}`, canonical: profile.siteUrl + "/archive", body });
}

export function renderAboutHtml(profile: CreatorProfile): string {
  const aboutMd = (profile.about && profile.about.trim()) ? profile.about : profile.bio;
  const body = `<article class="post">
<h1 class="post-title">About</h1>
<div class="post-body">
${mdToHtml(aboutMd || `Hi, I'm ${esc(profile.displayName)}.`)}
</div>
</article>`;
  return page(profile, { title: `About — ${profile.displayName}`, description: profile.bio, canonical: profile.siteUrl + "/about", body });
}

export function renderPostHtml(profile: CreatorProfile, post: Post): string {
  const tags = post.tags.length
    ? `<div class="post-tags">${post.tags.map((t) => `<a class="tag" href="/tag/${esc(slugify(t))}">${esc(t)}</a>`).join("")}</div>`
    : "";
  const ext = post.external ? `<p class="post-external"><a href="${esc(post.external)}" rel="noreferrer">↗ Original</a></p>` : "";
  const jsonLd = {
    "@context": "https://schema.org", "@type": "BlogPosting",
    headline: post.title, datePublished: post.publishedAt, dateModified: post.updatedAt,
    author: { "@type": "Person", name: profile.displayName },
    url: postUrl(profile, post.slug), description: post.summary,
  };
  const head = `<meta property="og:type" content="article" />
<script type="application/ld+json">${JSON.stringify(jsonLd)}</script>`;
  const body = `<article class="post">
<h1 class="post-title">${esc(post.title)}</h1>
<time class="post-date">${esc(fmtDate(post.publishedAt))}</time>
${tags}
${ext}
<div class="post-body">
${mdToHtml(post.body)}
</div>
</article>`;
  return page(profile, { title: post.title, description: post.summary, canonical: postUrl(profile, post.slug), body, head });
}

export function renderTagHtml(profile: CreatorProfile, tag: string, posts: Post[]): string {
  const body = `<h1 class="creator-name">#${esc(tag)}</h1>
<ul class="post-list">
${posts.map(postRow).join("\n")}
</ul>`;
  return page(profile, { title: `#${tag} — ${profile.displayName}`, description: `Posts tagged ${tag}`, canonical: `${profile.siteUrl}/tag/${slugify(tag)}`, body });
}

function renderReadme(profile: CreatorProfile): string {
  return `# ${profile.displayName} — published archive

Generated by [BKA](https://binary-blender.com/apps) at publish time and served statically by
Cloudflare Pages — no build step. Edit posts in BKA, not by hand.

- Live site: ${profile.siteUrl}
- Feed: ${profile.siteUrl}/feed.json
`;
}

// ─── full site ──────────────────────────────────────────────────────

export function renderSite(profile: CreatorProfile, posts: Post[], generatedAt: string): SiteFile[] {
  const sorted = [...posts].sort((a, b) => b.publishedAt.localeCompare(a.publishedAt));
  const files: SiteFile[] = [
    { path: "index.html", content: renderIndexHtml(profile, sorted) },
    { path: "archive.html", content: renderArchiveHtml(profile, sorted) },
    { path: "about.html", content: renderAboutHtml(profile) },
    { path: "style.css", content: themeCss(profile.theme, profile.accent) },
    { path: "feed.json", content: renderFeedJson(profile, sorted, generatedAt) },
    { path: "feed.xml", content: renderFeedXml(profile, sorted) },
    { path: "sitemap.xml", content: renderSitemap(profile, sorted) },
    { path: "robots.txt", content: renderRobots(profile) },
    { path: "theme.json", content: JSON.stringify({ theme: profile.theme, accent_color: profile.accent }, null, 2) + "\n" },
    { path: "README.md", content: renderReadme(profile) },
  ];

  // Per-post pages + markdown source + meta.
  for (const p of sorted) {
    files.push({ path: `posts/${p.slug}.html`, content: renderPostHtml(profile, p) });
    files.push({ path: `posts/${p.slug}.md`, content: p.body });
    files.push({ path: `posts/${p.slug}.meta.json`, content: JSON.stringify({
      slug: p.slug, title: p.title, summary: p.summary, tags: p.tags,
      kinds: p.kinds, publishedAt: p.publishedAt, updatedAt: p.updatedAt,
      language: p.language ?? profile.language, external: p.external ?? null,
    }, null, 2) + "\n" });
  }

  // Tag pages — one per unique tag (slugified for the URL).
  const byTag = new Map<string, { display: string; posts: Post[] }>();
  for (const p of sorted) {
    for (const t of p.tags) {
      const k = slugify(t);
      if (!byTag.has(k)) byTag.set(k, { display: t, posts: [] });
      byTag.get(k)!.posts.push(p);
    }
  }
  for (const [k, { display, posts: tagged }] of byTag) {
    files.push({ path: `tag/${k}.html`, content: renderTagHtml(profile, display, tagged) });
  }

  return files;
}
